import React from 'react';
import { Trophy, Star, Award } from 'lucide-react';
import { useTheme } from '../arrangement/ThemeContext';
export const AchievementsCardPublicView = ({ profileData, loading, username }) => {
  // Loading state
  const { colors } = useTheme(); 
  
  if (loading) {
    return (
      <div style={{
        background: colors.bgCard,
        borderRadius: '16px',
        padding: '20px',
        border: '2px solid #333',
        color: '#e5e5e5',
        marginBottom: '24px',
        minHeight: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', color: '#888' }}>
          Loading achievements...
        </div>
      </div>
    );
  }

  const displayedAchievements = profileData?.displayed_achievements || [];
  const allAchievements = profileData?.achievements || [];
  const totalCount = allAchievements.length;
  const completedCount = allAchievements.filter(a => a.progress === 100).length;

  return (
    <div style={{
      background: colors.bgCard,
      borderRadius: '16px',
      padding: '20px',
      border: '2px solid #333',
      color: '#e5e5e5',
      marginBottom: '24px',
      position: 'relative'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <h3 style={{
          margin: 0,
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#e5e5e5'
        }}>
          <Trophy size={18} style={{ color: '#fbbf24' }} />
          {username ? `${username}'s Achievements` : 'Achievements'}
        </h3>
        
        {/* Achievement stats */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '12px',
          color: '#9ca3af'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            background: 'rgba(16, 185, 129, 0.1)',
            padding: '4px 8px',
            borderRadius: '8px',
            border: '1px solid rgba(16, 185, 129, 0.2)'
          }}>
            <Star size={12} style={{ color: '#10b981' }} />
            <span style={{ color: '#10b981', fontWeight: '500' }}>
              {completedCount} Completed
            </span>
          </div>
          
          {totalCount > displayedAchievements.length && (
            <div style={{
              color: '#6b7280',
              fontWeight: '500'
            }}>
              {displayedAchievements.length} of {totalCount} shown
            </div>
          )}
        </div>
      </div>
      
      {/* Achievements Grid */}
      {displayedAchievements.length > 0 ? (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', 
          gap: '12px' 
        }}>
          {displayedAchievements.map((achievement, index) => (
            <div 
              key={achievement.id} 
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '16px 12px',
                borderRadius: '12px',
                background: achievement.progress === 100 
                  ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                  : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                border: '2px solid',
                borderColor: achievement.progress === 100 ? '#fbbf24' : '#4b5563',
                opacity: achievement.progress === 100 ? 1 : 0.7,
                transition: 'all 0.3s ease',
                position: 'relative',
                cursor: 'default'
              }}
              title={`${achievement.name}: ${achievement.description}${achievement.progress < 100 ? ` (${achievement.progress}% complete)` : ''}`}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = achievement.progress === 100 
                  ? '0 8px 25px rgba(251, 191, 36, 0.3)'
                  : '0 8px 25px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Achievement Icon */}
              <span style={{ 
                fontSize: '24px', 
                marginBottom: '8px',
                filter: achievement.progress === 100 ? 'none' : 'grayscale(100%)'
              }}>
                {achievement.icon || '🏆'}
              </span>
              
              {/* Achievement Name */}
              <span style={{
                fontSize: '10px',
                fontWeight: '600',
                textAlign: 'center',
                color: achievement.progress === 100 ? '#92400e' : '#e5e7eb',
                lineHeight: '1.2',
                marginBottom: achievement.progress < 100 && achievement.progress > 0 ? '4px' : 0
              }}>
                {achievement.name}
              </span>
              
              {/* Progress indicator for incomplete achievements */}
              {achievement.progress < 100 && achievement.progress > 0 && (
                <div style={{
                  fontSize: '8px',
                  color: '#fbbf24',
                  fontWeight: '500',
                  background: 'rgba(251, 191, 36, 0.1)',
                  padding: '2px 6px',
                  borderRadius: '4px'
                }}>
                  {achievement.progress}%
                </div>
              )}
              
              {/* Completion indicator */}
              {achievement.progress === 100 && (
                <div style={{
                  position: 'absolute',
                  top: '6px',
                  right: '6px',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: '#10b981',
                  border: '2px solid #fef3c7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Award size={6} style={{ color: '#fef3c7' }} />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* Empty state */
        <div style={{
          textAlign: 'center',
          padding: '32px 16px',
          color: '#888'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: 'linear-gradient(135deg, #374151 0%, #4b5563 100%)',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto',
            border: '2px dashed #555'
          }}>
            <Trophy size={32} style={{ color: '#888', opacity: 0.5 }} />
          </div>
          
          <h4 style={{
            margin: '0 0 8px 0',
            fontSize: '16px',
            fontWeight: '600',
            color: '#e5e5e5'
          }}>
            No Achievements Displayed
          </h4>
          
          <p style={{
            margin: 0,
            fontSize: '14px',
            color: '#888',
            lineHeight: '1.4'
          }}>
            {totalCount > 0 
              ? `${username || 'This user'} hasn't selected any achievements to display publicly`
              : `${username || 'This user'} hasn't unlocked any achievements yet`
            }
          </p>
          
          {totalCount > 0 && (
            <p style={{
              margin: '4px 0 0 0',
              fontSize: '12px',
              color: '#6b7280'
            }}>
              {completedCount} of {totalCount} achievements completed
            </p>
          )}
        </div>
      )}
      
      {/* Footer stats for users with many achievements */}
      {totalCount > 6 && displayedAchievements.length > 0 && (
        <div style={{
          marginTop: '16px',
          paddingTop: '12px',
          borderTop: '1px solid #374151',
          textAlign: 'center'
        }}>
          <p style={{
            margin: 0,
            fontSize: '12px',
            color: '#9ca3af'
          }}>
            Showing {displayedAchievements.length} of {totalCount} total achievements
            {completedCount < totalCount && (
              <span style={{ color: '#6b7280' }}>
                {' '}• {totalCount - completedCount} in progress
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};